import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  CardMedia,
} from "@mui/material";
import { WarehouseData } from "./warehouses.types";
import { API_URL } from "../../config/config";

const Warehouses: React.FC = () => {
  const { user } = useAuth();
  const [warehouse, setWarehouse] = useState<WarehouseData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStore = async () => {
      try {
        setLoading(true);
        if (!user) return;
        const res = await fetch(`${API_URL}/warehouse/${user.id}/`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || "Failed to fetch warehouse");
        }
        const data: any = await res.json();
        setWarehouse(data.data);
      } catch (err: any) {
        setError(err.message || "Unexpected error");
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
  }, [user]);

  if (!user) return null;

  const goToWarehouse = (id: string) => {
    navigate(`/warehouse/${id}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {error}
      </Alert>
    );
  }

  if (!warehouse || warehouse.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 4 }}>
        No warehouse found.
      </Alert>
    );
  }

  return (
    <Box
      p={2}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        justifyContent: { xs: "center", md: "flex-start" },
      }}
    >
      {warehouse.map((s: WarehouseData) => (
        <Card
          key={s.id}
          onClick={() => goToWarehouse(s.id)}
          sx={{
            width: 280,
            cursor: "pointer",
            transition: "transform 0.2s",
            "&:hover": { transform: "scale(1.03)" },
          }}
        >
          {s.image_url && (
            <CardMedia
              component="img"
              height="160"
              image={s.image_url}
              alt={s.name}
            />
          )}
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {s.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {s.address}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              📧 {s.email}
            </Typography>
            {s.phone_number && (
              <Typography variant="body2" color="text.secondary">
                ☎ {s.phone_number}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              Region: {s.region} | City: {s.city}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default Warehouses;
